import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Spade,
  Trophy,
  Fish,
  Gamepad2,
  ShieldCheck,
  Zap,
  Library,
  Gift,
  Smartphone,
  Headphones,
  Sparkles,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Marquee from "@/components/Marquee";
import {
  BRAND,
  LINKS,
  GAME_CATEGORIES,
  GAME_GALLERY,
} from "@/data/site";
import { useTranslation } from "@/i18n/LanguageContext";

const ICONS = {
  Spade,
  Trophy,
  Fish,
  Gamepad2,
  ShieldCheck,
  Zap,
  Library,
  Gift,
  Smartphone,
  Headphones,
};

const HERO_IMAGES = [GAME_GALLERY[0], GAME_GALLERY[3], GAME_GALLERY[4]];
const HERO_HREFS = [LINKS.register, LINKS.login, LINKS.login];

export default function Home() {
  const [slide, setSlide] = useState(0);
  const { t } = useTranslation();
  const HERO_SLIDES = t.hero.slides.map((s, i) => ({
    ...s,
    href: HERO_HREFS[i],
    image: HERO_IMAGES[i],
  }));

  useEffect(() => {
    const id = setInterval(
      () => setSlide((s) => (s + 1) % HERO_SLIDES.length),
      5500
    );
    return () => clearInterval(id);
  }, []);

  const cur = HERO_SLIDES[slide];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-25" />
        <div className="absolute inset-0 bg-noise opacity-50 mix-blend-overlay" />
        <div
          aria-hidden
          className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-orange-500/20 blur-[120px]"
        />
        <div
          aria-hidden
          className="absolute -bottom-40 -right-40 w-[640px] h-[640px] rounded-full bg-orange-700/15 blur-[140px]"
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          {/* Text */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 ring-1 ring-orange-500/30 text-orange-200 text-xs tracking-[0.25em] uppercase">
              <Sparkles className="w-3.5 h-3.5" /> {t.hero.tag}
            </div>

            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-wider text-zinc-50">
              {t.hero.titleA}
              <span className="block text-orange-grad text-glow">
                {t.hero.titleB}
              </span>
              <span className="block text-zinc-400 text-3xl sm:text-4xl lg:text-5xl mt-2">
                {t.hero.titleC}
              </span>
            </h1>

            <p className="mt-6 max-w-xl text-zinc-400 text-base leading-relaxed">
              {t.hero.desc}{" "}
              <span className="text-orange-300">{t.hero.desc2}</span>
              {t.hero.desc3}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                asChild
                size="lg"
                data-testid="hero-register-btn"
                className="rounded-full px-7 h-12 bg-gradient-to-b from-orange-400 to-orange-600 text-black font-semibold shine glow-orange"
              >
                <a href={LINKS.register} target="_blank" rel="noreferrer">
                  {t.common.registerFree} <ChevronRight className="w-4 h-4 ml-1" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                data-testid="hero-login-btn"
                className="rounded-full px-7 h-12 border-orange-500/40 bg-transparent text-orange-200 hover:bg-orange-500/10"
              >
                <a href={LINKS.login} target="_blank" rel="noreferrer">
                  {t.common.loginNow}
                </a>
              </Button>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { v: "500+", l: t.hero.stats.games },
                { v: "30+", l: t.hero.stats.providers },
                { v: "3 min", l: t.hero.stats.withdraw },
              ].map((s) => (
                <div
                  key={s.l}
                  className="rounded-2xl border border-orange-500/15 bg-white/[0.02] px-4 py-3 backdrop-blur-sm"
                >
                  <div className="font-display text-2xl text-orange-grad">{s.v}</div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 mt-0.5">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero card carousel */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-orange-500/40 via-orange-700/20 to-transparent blur-2xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-orange-500/30 bg-[#0c0c10] glow-orange">
                <div className="aspect-[4/5] sm:aspect-[5/6] relative">
                  {HERO_SLIDES.map((s, i) => (
                    <div
                      key={i}
                      className={`absolute inset-0 transition-opacity duration-700 ${
                        i === slide ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      <img
                        src={s.image}
                        alt={s.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 banner-fade" />
                    </div>
                  ))}

                  <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                    <div className="text-orange-300 text-xs tracking-[0.3em] uppercase">
                      {cur.eyebrow}
                    </div>
                    <h3 className="font-display text-3xl sm:text-4xl tracking-wider text-white mt-2">
                      {cur.title}
                    </h3>
                    <p className="mt-2 text-sm text-zinc-300/90 max-w-sm">
                      {cur.desc}
                    </p>
                    <div className="mt-4 flex items-center gap-3">
                      <Button
                        asChild
                        data-testid={`hero-slide-cta-${slide}`}
                        className="rounded-full bg-orange-500 hover:bg-orange-400 text-black font-semibold"
                      >
                        <a href={cur.href} target="_blank" rel="noreferrer">
                          {cur.cta}
                        </a>
                      </Button>
                      <div className="flex gap-1.5">
                        {HERO_SLIDES.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setSlide(i)}
                            aria-label={`Slide ${i + 1}`}
                            className={`h-1.5 rounded-full transition-all ${
                              i === slide
                                ? "w-8 bg-orange-400"
                                : "w-3 bg-white/30"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee />

      {/* GAME CATEGORIES */}
      <section className="relative py-20" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <SectionHeading
            eyebrow={t.categories.eyebrow}
            title={t.categories.title}
            sub={t.categories.sub}
          />

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {GAME_CATEGORIES.map((c, idx) => {
              const Icon = ICONS[c.icon] || Gamepad2;
              const tc = t.categories[c.id] || { name: c.name, desc: c.desc };
              return (
                <Link
                  to="/game"
                  key={c.id}
                  data-testid={`category-${c.id}`}
                  style={{ animationDelay: `${idx * 80}ms` }}
                  className="tilt group relative rounded-2xl overflow-hidden border border-orange-500/15 bg-[#0c0c10]"
                >
                  <div className="aspect-[3/4] relative">
                    <img
                      src={c.image}
                      alt={tc.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#07070a] via-[#07070a]/55 to-transparent" />
                    <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-orange-500 text-black flex items-center justify-center glow-orange-soft">
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <div className="font-display text-3xl tracking-wider text-orange-grad">
                      {tc.name}
                    </div>
                    <p className="mt-1.5 text-xs text-zinc-400 line-clamp-2">
                      {tc.desc}
                    </p>
                    <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-orange-300">
                      {t.common.playNow} <ChevronRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROMOTIONS */}
      <section
        className="relative py-20 border-y border-orange-500/15 bg-gradient-to-b from-[#0a0a0e] to-[#07070a]"
        data-testid="promos-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <SectionHeading
            eyebrow={t.promos.eyebrow}
            title={t.promos.title}
            sub={t.promos.sub}
          />

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {t.promos.items.map((p, i) => (
              <div
                key={p.title}
                data-testid={`promo-${i}`}
                className="tilt relative rounded-2xl border border-orange-500/20 bg-gradient-to-br from-[#15100a] to-[#0a0a0d] p-7 overflow-hidden"
              >
                <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-orange-500/15 blur-3xl" />
                <div className="relative">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-orange-300/80">
                    {p.badgeLabel}
                  </div>
                  <div className="font-display text-7xl text-orange-grad text-glow leading-none mt-2">
                    {p.badge}
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-zinc-50">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                    {p.desc}
                  </p>
                  <Button
                    asChild
                    data-testid={`promo-cta-${i}`}
                    className="mt-6 rounded-full bg-orange-500 hover:bg-orange-400 text-black font-semibold"
                  >
                    <a href={LINKS.register} target="_blank" rel="noreferrer">
                      {p.cta} <ChevronRight className="w-4 h-4 ml-1" />
                    </a>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="relative py-20" data-testid="reasons-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <SectionHeading
            eyebrow={t.reasons.eyebrow}
            title={t.reasons.title}
            sub={t.reasons.sub}
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.reasons.items.map((r) => {
              const Icon = ICONS[r.icon] || Star;
              return (
                <div
                  key={r.title}
                  className="group p-6 rounded-2xl border border-orange-500/15 bg-[#0b0b0f] hover:border-orange-500/40 transition"
                >
                  <div className="w-12 h-12 rounded-xl bg-orange-500/10 ring-1 ring-orange-500/30 flex items-center justify-center text-orange-300 group-hover:bg-orange-500 group-hover:text-black transition">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-5 font-display text-2xl tracking-wider text-zinc-50">
                    {r.title}
                  </h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                    {r.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-20 border-t border-orange-500/15" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
          <SectionHeading
            eyebrow={t.faq.eyebrow}
            title={t.faq.title}
            sub={t.faq.sub}
            align="left"
          />
          <Accordion type="single" collapsible className="mt-10">
            {t.faq.items.map((f, i) => (
              <AccordionItem
                key={i}
                value={`q-${i}`}
                data-testid={`faq-${i}`}
                className="border-orange-500/15"
              >
                <AccordionTrigger className="text-left font-display tracking-wider text-lg text-zinc-100 hover:no-underline hover:text-orange-300">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 text-sm leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20" data-testid="cta-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="relative rounded-3xl overflow-hidden border border-orange-500/30 bg-gradient-to-br from-[#1a0e04] via-[#120a04] to-[#07070a] p-10 sm:p-14 glow-orange-soft">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-orange-500/25 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-orange-700/20 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-orange-300 text-xs tracking-[0.3em] uppercase">
                  {t.cta.eyebrow}
                </div>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-wider text-zinc-50">
                  {t.cta.titleA} <span className="text-orange-grad">{t.cta.titleB}</span>
                </h2>
                <p className="mt-4 text-zinc-400 max-w-md">
                  {t.cta.desc}
                </p>
              </div>
              <div className="flex md:justify-end gap-3">
                <Button
                  asChild
                  size="lg"
                  data-testid="cta-register-btn"
                  className="rounded-full h-12 px-7 bg-gradient-to-b from-orange-400 to-orange-600 text-black font-semibold shine glow-orange"
                >
                  <a href={LINKS.register} target="_blank" rel="noreferrer">
                    {t.common.registerFree}
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  data-testid="cta-login-btn"
                  className="rounded-full h-12 px-7 border-orange-500/40 text-orange-200 hover:bg-orange-500/10"
                >
                  <a href={LINKS.login} target="_blank" rel="noreferrer">
                    {t.common.loginNow}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHeading({ eyebrow, title, sub, align = "center" }) {
  const isCenter = align === "center";
  return (
    <div className={`max-w-2xl ${isCenter ? "mx-auto text-center" : ""}`}>
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 ring-1 ring-orange-500/30 text-orange-200 text-[10px] tracking-[0.3em] uppercase">
        <Star className="w-3 h-3" /> {eyebrow}
      </div>
      <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-wider text-zinc-50">
        {title}
      </h2>
      {sub && <p className="mt-3 text-zinc-400 text-base">{sub}</p>}
      <div className={`mt-5 divider-thread ${isCenter ? "max-w-xs mx-auto" : "max-w-xs"}`} />
    </div>
  );
}
